import BaseLayer from "ol/layer/Base"
import TileLayer from "ol/layer/Tile"
import WebGLTileLayer from "ol/layer/WebGLTile"
import DataTileSource from "ol/source/DataTile"
import { DatasetCache, ModelOutputCache } from "../map/map_view"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from "../modelling/tile_grid"
import { DatasetLayer, ModelOutputLayer } from "../state"
import colormap from "colormap"
import distinctColors from "distinct-colors"
import {
  categoricalStyleCacheKey,
  labelEntriesFromGrid,
  resolveCategoricalPalette,
} from "../layers/categorical_layer_colors"
import {
  categoricalNumericViewCacheKey,
  categoricalViewCacheKey,
  isCategoricalTableRowView,
  numericBoundsForTableRowView,
  resolveTableRowMappingForLayer,
  type CategoricalTableRowMapping,
} from "../layers/categorical_layer_view"
import { Data } from "rete/types/core/data"

type CategoricalNumericViewConfig = CategoricalTableRowMapping & {
  bounds: [number, number] | null
  cacheKey: string
}

class ModelOutputSource extends DataTileSource {
  readonly tileLayer: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid
  readonly numericViewKey: string | null

  constructor(
    tileLayer: BooleanTileGrid | NumericTileGrid | CategoricalTileGrid,
    bounds: [number, number] | null,
    numericView?: CategoricalNumericViewConfig | null
  ) {
    super({
      loader: (z, x, y) => {
        const catNumeric = numericView ?? null
        const cat = tileLayer instanceof CategoricalTileGrid && !catNumeric
        let [min, max] = [0, 1]

        if (tileLayer instanceof CategoricalTileGrid) {
          if (catNumeric) {
            [min, max] = catNumeric.bounds ?? numericBoundsForTableRowView(
              { overrideBounds: false, bounds: undefined } as ModelOutputLayer,
              catNumeric
            )
          } else {
            [min, max] = tileLayer.getMinMax()
          }
        } else if (tileLayer instanceof NumericTileGrid) {
          [min, max] = bounds !== null ? bounds : tileLayer.getMinMax()
        }

        const image = new Float32Array(256 * 256)
        for (let pixelX = 0; pixelX < 256; ++pixelX) {
          for (let pixelY = 0; pixelY < 256; ++pixelY) {
            const tileX = (x + (pixelX / 256)) * Math.pow(2, tileLayer.zoom - z)
            const tileY = (y + (pixelY / 256)) * Math.pow(2, tileLayer.zoom - z)
            const tile = tileLayer.get(tileX, tileY)
            let val = typeof tile === "number" ? tile : (tile ? 1 : 0)
            if (catNumeric && tileLayer instanceof CategoricalTileGrid) {
              if (val === 255) {
                image[pixelY * 256 + pixelX] = -1
              } else {
                const mapped = catNumeric.valuesByCategoryKey.get(val)
                if (mapped === undefined || Number.isNaN(mapped)) {
                  image[pixelY * 256 + pixelX] = -1
                } else {
                  let normalized = mapped
                  if (catNumeric.bounds) {
                    if (normalized > max) normalized = max
                    if (normalized < min) normalized = min
                  }
                  image[pixelY * 256 + pixelX] = (normalized - min) / (max - min)
                }
              }
            } else if (cat) {
              if (val > max) {
                image[pixelY * 256 + pixelX] = 0
              } else {
                image[pixelY * 256 + pixelX] = val
              }
            } else {
              // if the value is NaN, assign it to -1 so that it is transparent
              if (isNaN(val)) image[pixelY * 256 + pixelX] = -1
              else {
                if (bounds) {
                  if (val > max) val = max
                  if (val < min) val = min
                }

                image[pixelY * 256 + pixelX] = (val - min) / (max - min)
              }
            }
          }
        }
        return image
      },
      maxZoom: tileLayer.zoom,
      bandCount: 1,
      // A short transition avoids hard tile swaps while panning.
      transition: 220,
    })
    this.tileLayer = tileLayer
    this.numericViewKey = numericView?.cacheKey ?? null
  }
}
export function getColorStops(name: string, steps: number): any[] {

  const delta = (0 - 1) / (steps - 1)
  const stops: Array<any> = []

  let colors: any[] = []

  colors = colormap({ colormap: name === "BuRd" ? "RdBu" : name, nshades: steps, format: 'rgba' })

  if (name != "BuRd") {
    colors = colors.reverse()
  }
  
  for (let i = 0; i < steps; i++) {
    stops.push(1 + i * delta)
    stops.push(colors[i])
  }

  return stops
}

export function getCatColorStops(palette: [number, number, number, number][] | undefined, n: number): any[] {

  const arr: Array<any> = []

  arr.push('case')

  for (let key = n; key >= 1; key--) {
    arr.push(['>=', ['band', 1], key])

    let rgba = palette ? palette[key - 1] : [0, 0, 0, 0]

    arr.push(rgba)

  }
  arr.push([0, 0, 0, 0])

  return arr

}


const styleOutputCache: Map<number, string> = new Map()
const catStyleCache: Map<number, string> = new Map()
const viewStyleCache: Map<number, string> = new Map()
const boundsCache: Map<number, [boolean, [number, number] | undefined]> = new Map()

function resolveCategoricalNumericView(
  layer: ModelOutputLayer | DatasetLayer,
  tileLayer: CategoricalTileGrid,
  model: Data | null
): CategoricalNumericViewConfig | null {
  if (!isCategoricalTableRowView(layer)) return null
  const mapping = resolveTableRowMappingForLayer(model, layer, tileLayer)
  if (!mapping) return null

  const bounds = layer.overrideBounds && layer.bounds ? layer.bounds : null
  return {
    ...mapping,
    bounds,
    cacheKey: categoricalNumericViewCacheKey(layer, mapping),
  }
}

function resolveCategoricalLayerPalette(
  tileLayer: CategoricalTileGrid,
  layer: ModelOutputLayer | DatasetLayer
): [number, number, number, number][] {
  const maxKey = tileLayer.getMinMax()[1]
  const labels = labelEntriesFromGrid(tileLayer)
  const defaultCols = distinctColors({ count: Math.max(maxKey, 1) }).map(e => e.rgba()) as [number, number, number, number][]

  if (layer.colors === undefined) {
    layer.colors = []
  }

  for (let x = 0; x < maxKey; x++) {
    layer.colors[x] = layer.colors[x] ?? defaultCols[x] ?? [0, 0, 0, 0]
  }

  return resolveCategoricalPalette(
    labels,
    maxKey,
    { colors: layer.colors, colorByLabel: layer.colorByLabel },
    index => defaultCols[index] ?? [0, 0, 0, 0]
  )
}

export function reifyModelOutputLayer(
  layer: ModelOutputLayer | DatasetLayer,
  existingLayer: BaseLayer | null,
  outputCache: ModelOutputCache | DatasetCache,
  loadteamDataset: (layer: DatasetLayer) => void,
  model: Data | null = null
) {

  // to avoid conflicts between model output and dataset layers, we use negative ids for model output layers
  const [ModelId, CacheId] = layer.type === "ModelOutputLayer" ? [layer.nodeId, layer.nodeId] : [layer.id, -layer.id]

  if (!(ModelId in outputCache)) {
    // in scenario that layers are saved and loaded, we need to ensure that the layer is loaded
    if (layer.type === "DatasetLayer") {
      loadteamDataset(layer)
    }
    return new TileLayer()
  }

  const tileLayer = outputCache[ModelId]
  const numericView = tileLayer instanceof CategoricalTileGrid
    ? resolveCategoricalNumericView(layer, tileLayer, model)
    : null
  const currentViewKey = numericView?.cacheKey ?? categoricalViewCacheKey(layer)

  // Check if we can reuse the existing layer
  if (existingLayer instanceof WebGLTileLayer) {
    const source = existingLayer.getSource()

    if (source instanceof ModelOutputSource &&
      source.tileLayer === tileLayer) {
      
      const cachedBounds = boundsCache.get(CacheId)
      const currentBounds = [layer.overrideBounds, layer.bounds]
      const boundsChanged = cachedBounds?.toString() !== currentBounds.toString()
      const viewChanged = viewStyleCache.get(CacheId) !== currentViewKey
      const numericViewChanged = source.numericViewKey !== (numericView?.cacheKey ?? null)
      
      // If bounds or view changed, we need to recreate the source (bounds/view affect data processing)
      if (!boundsChanged && !viewChanged && !numericViewChanged) {
        // Only style changed, update style in place
        const cachedFill = styleOutputCache.get(CacheId)
        const cachedStyleKey = catStyleCache.get(CacheId)
        const currentStyleKey = tileLayer instanceof CategoricalTileGrid && !numericView
          ? categoricalStyleCacheKey({ colors: layer.colors, colorByLabel: layer.colorByLabel })
          : layer.colors?.toString() ?? ""
        const fillChanged = cachedFill !== layer.fill
        const colorsChanged = cachedStyleKey !== currentStyleKey

        // OL 6's WebGLTileLayer.setStyle doesn't reliably re-render categorical
        // `'case'` style expressions when only the RGBA constants change (e.g.
        // copying a palette between two categorical layers). Fall through to
        // recreate the WebGLTileLayer in that case so the new palette is
        // guaranteed to take effect on the map.
        if (tileLayer instanceof CategoricalTileGrid && !numericView) {
          if (!colorsChanged) {
            // For categorical layers, fill has no visual effect; nothing to do.
            styleOutputCache.set(CacheId, layer.fill)
            return existingLayer
          }
          // colors changed: fall through to recreate the layer + source below.
        } else if (fillChanged || colorsChanged) {
          // Update the style cache
          styleOutputCache.set(CacheId, layer.fill)
          catStyleCache.set(CacheId, layer.colors?.toString() ?? "")

          // Build new color style
          let color: any[] = []

          if (tileLayer instanceof BooleanTileGrid) {
            if (layer.colors === undefined || layer.colors.length !== 2) layer.colors = [[0, 0, 0, 1], [255, 255, 255, 1]]
            color = [
              'case',
              ['==', ['band', 1], 0],
              [0, 0, 0, 0],
              layer.colors[1]
            ]
          } else {
            color = [
              'case',
              ['==', ['band', 1], -1],
              [0, 0, 0, 0],
              ['interpolate',
                ['linear'],
                ['band', 1],
                ...getColorStops(layer.fill === "heatmap" ? "jet" : (layer.fill === "greyscale" ? "greys" : layer.fill), 100)]
            ]
          }

          // Update style in place
          existingLayer.setStyle({ color })
          existingLayer.changed()
          return existingLayer
        } else {
          // Nothing changed, return existing layer
          return existingLayer
        }
      }
    }
  }

  // Need to create a new layer (either no existing layer, or source/bounds/view changed)
  styleOutputCache.set(CacheId, layer.fill)
  viewStyleCache.set(CacheId, currentViewKey)
  if (tileLayer instanceof CategoricalTileGrid && !numericView) {
    catStyleCache.set(CacheId, categoricalStyleCacheKey({ colors: layer.colors, colorByLabel: layer.colorByLabel }))
  } else {
    catStyleCache.set(CacheId, layer.colors?.toString() ?? "")
  }
  boundsCache.set(CacheId, [layer.overrideBounds, layer.bounds])

  let color: any[] = []

  if (tileLayer instanceof CategoricalTileGrid && !numericView) {
    const palette = resolveCategoricalLayerPalette(tileLayer, layer)
    color = getCatColorStops(palette, tileLayer.getMinMax()[1])

  }else if (tileLayer instanceof BooleanTileGrid) {

    if (layer.colors === undefined || layer.colors.length !== 2) layer.colors = [[0, 0, 0, 1], [255, 255, 255, 1]]

    color = [
      'case',
      ['==', ['band', 1], 0],
      [0, 0, 0, 0],
      layer.colors[1]
    ]

  }else {

    color = [
      'case',
      // if the value is NaN, make it transparent
      ['==', ['band', 1], -1],
      [0, 0, 0, 0],
      ['interpolate',
        ['linear'],
        ['band', 1],
        ...getColorStops(layer.fill === "heatmap" ? "jet" : (layer.fill === "greyscale" ? "greys" : layer.fill), 100)]
    ]


  }


  return new WebGLTileLayer({
    source: new ModelOutputSource(
      tileLayer,
      (layer.overrideBounds && layer.bounds) ? layer.bounds : null,
      numericView
    ),
    style: {
      color,
    },
  })
}
